// serviceCategories.js
//
// The fixed ThinkSpatial Service_Category taxonomy and the pure
// `filterByCategory` helper used by the Projects page filter and the Home
// services summary.
//
// The taxonomy is a brand-defined enumeration. The Projects filter additionally
// offers an `All` option that returns the project list unchanged. Categories
// that appear in API data but are not part of this enumeration are still
// displayed and remain filterable under their own label (the filter matches on
// the project's `serviceCategory` value, not on enumeration membership), so
// this list is the canonical set of *brand* categories, not a whitelist.
//
// Requirement 6.3.

/**
 * The fixed Service_Category enumeration, in display order.
 * @type {ReadonlyArray<string>}
 */
export const SERVICE_CATEGORIES = Object.freeze([
  'Civil Surveying',
  'Asset Capture',
  'Surveying Services',
  'Automated Monitoring',
  'UAV',
  'Spatial Software'
])

/**
 * The sentinel selection that means "do not filter" — returns every project.
 * @type {string}
 */
export const ALL_CATEGORIES = 'All'

/**
 * The full list of selectable filter options: `All` followed by the fixed
 * Service_Category enumeration.
 * @type {ReadonlyArray<string>}
 */
export const CATEGORY_FILTER_OPTIONS = Object.freeze([
  ALL_CATEGORIES,
  ...SERVICE_CATEGORIES
])

/**
 * Filter a list of normalized projects by Service_Category selection.
 *
 * Pure and total: never mutates its input and never throws.
 * - When `selection` is `All` (the {@link ALL_CATEGORIES} sentinel), the list is
 *   returned unchanged (sound + complete: nothing dropped, nothing added).
 * - Otherwise, returns exactly the projects whose `serviceCategory` equals the
 *   selection. Every returned project matches the selection, and no matching
 *   project is omitted.
 *
 * @param {Array<{serviceCategory?: string}>} projects - normalized projects
 * @param {string} selection - a Service_Category or {@link ALL_CATEGORIES}
 * @returns {Array} the filtered projects (a new array)
 */
export function filterByCategory (projects, selection) {
  if (!Array.isArray(projects)) return []
  if (selection === ALL_CATEGORIES) return projects.slice()
  return projects.filter(project => {
    if (!project || typeof project !== 'object') return false
    // A project may carry multiple Service_Category selections
    // (`serviceCategories`); it matches the filter if ANY of them equals the
    // selection. Fall back to the single legacy `serviceCategory` field when
    // the array is absent so older/partial payloads still filter correctly.
    if (Array.isArray(project.serviceCategories)) {
      return project.serviceCategories.includes(selection)
    }
    return project.serviceCategory === selection
  })
}

export default filterByCategory
